import { useRef, useState } from 'react';
import { Alert, Form, Input, Modal, Typography } from 'antd';

type ReasonAction = {
  title: string; context: string; label?: string; danger?: boolean;
  submit: (reason: string) => Promise<unknown>;
  afterSave?: () => Promise<unknown>;
};

/** App-owned dialog also works in hosts that suppress native browser prompts. */
export function useReasonAction(notify: (text: string) => void) {
  const [action, setAction] = useState<ReasonAction>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submitting = useRef(false);
  const [form] = Form.useForm<{ reason: string }>();
  const open = (next: ReasonAction) => { setError(''); form.resetFields(); setAction(next); };
  const submit = async ({ reason }: { reason: string }) => {
    if (!action || submitting.current) return;
    submitting.current = true; setBusy(true); setError('');
    try {
      await action.submit(reason.trim());
      setAction(undefined); notify(`${action.title}成功`);
      // A failed refresh must not leave a successful write available for resubmission.
      try { await action.afterSave?.(); } catch (failure) { notify(`操作已保存，列表刷新失败：${String(failure)}`); }
    } catch (failure) { setError(String(failure)); }
    finally { submitting.current = false; setBusy(false); }
  };
  const dialog = <Modal open={Boolean(action)} title={action?.title} okText="确认提交" cancelText="取消"
    confirmLoading={busy} okButtonProps={{ danger: action?.danger }} cancelButtonProps={{ disabled: busy }}
    closable={!busy} maskClosable={false} onCancel={() => { if (!submitting.current) setAction(undefined); }} onOk={() => form.submit()}>
    <Typography.Paragraph type="secondary">{action?.context}</Typography.Paragraph>
    {error && <Alert type="error" showIcon title={error} />}
    <Form form={form} layout="vertical" onFinish={submit}>
      <Form.Item name="reason" label={action?.label ?? '操作原因'} rules={[{ required: true, whitespace: true, message: '请填写后再提交' }]}>
        <Input.TextArea autoFocus rows={4} maxLength={2000} showCount disabled={busy} />
      </Form.Item>
    </Form>
  </Modal>;
  return { open, dialog };
}
